import { Builder, BuilderComponent, builder } from '@builder.io/react';
import { graphql } from 'gatsby';
import { Suspense } from 'react';
import customComponentsRegistration, { richTextModels } from 'templates/componentGenerator';

import ModalContainer from 'molecules/Modal/Modal';

import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import BlogHero from 'components/Hero/HeroBlog/HeroBlog';
import Main from 'components/Main';
import SEO from 'components/seo';

import type { Query } from 'graphqlTypes';
import type { FC } from 'react';
import type { IHead } from 'templates/BuilderPage';

builder.init(process.env.GATSBY_BUILDER_KEY || '');

const PostTemplate = (query: { data: Query }) => {
  const {
    data: {
      allBuilderModels: {
        onePost,
        oneGlobalBanner,
        oneComponentHeader: header,
        oneComponentFooter: footer,
        blogPostCategory,
        resourcesCategory,
        hiddenFormFields,
      },
    },
  } = query;

  const post = onePost?.content;
  const { banner } = { ...onePost?.data };
  const globalBanner = oneGlobalBanner?.data;

  const isCustomBanner = banner?.customBanner === true;
  const isGlobalBanner = globalBanner?.showBanner === true;
  const isHideBanner = banner?.hideBanner === true;

  customComponentsRegistration();

  return (
    <Suspense fallback>
      {header?.content && (
        <Header
          {...header.content.data}
          banner={(isCustomBanner && !isHideBanner && banner) || (isGlobalBanner && !isHideBanner && globalBanner)}
        />
      )}
      <Main>
        <BlogHero blogPost={post} />
        <BuilderComponent
          model="post"
          data={{
            template: 'post',
            entryCategories: JSON.stringify({ blogPostCategory, resourcesCategory }),
            hiddenFields: JSON.stringify({ hiddenFormFields }),
          }}
          content={post}
          options={{ includeRefs: true, cachebust: true }}
        />
      </Main>
      {footer?.content && <Footer isDark={true} content={footer.content.data} />}
      <ModalContainer />
    </Suspense>
  );
};

export default PostTemplate;

export const Head: FC<IHead> = ({
  data: {
    allBuilderModels: { onePost },
  },
  location,
}) => (
  <>
    {onePost && (
      <SEO
        title={onePost?.data?.metaTitle || onePost?.data?.title}
        description={onePost?.data?.metaDescription || onePost?.data?.description}
        image={onePost?.data?.featuredImage}
        location={location}
        indexable={!onePost?.data?.noIndex}
      />
    )}
  </>
);

export const postQuery = graphql`
  query getPosts($path: String!) {
    allBuilderModels {
      onePost(target: { urlPath: $path }, options: { includeRefs: true, cachebust: true }) {
        content
        data {
          title
          description
          featuredImage
          url
          noIndex
          banner
          metaTitle
          metaDescription
        }
      }
      oneGlobalBanner(
        options: { includeRefs: true, cachebust: true }
        target: { id: "b241c59d043b4cc7b0f608356bea42ef" }
      ) {
        data {
          showBanner
          body
          button
          heading
          includeIcon
        }
      }
      oneComponentHeader(
        options: { includeRefs: true, cachebust: true }
        target: { id: "8fe1129198a9478c9cc7346a40a63805" }
      ) {
        content
      }
      oneComponentFooter(
        options: { includeRefs: true, cachebust: true }
        target: { id: "a7c491a16336403c94432e7b3ebb419b" }
      ) {
        content
      }
      blogPostCategory(options: { includeRefs: true, cachebust: true }) {
        content
      }
      resourcesCategory(options: { includeRefs: true, cachebust: true }) {
        content
      }
      hiddenFormFields {
        data {
          hiddenFormField
        }
      }
    }
  }
`;

Builder.register('insertMenu', {
  name: 'Blog Components',
  advanced: true,
  items: richTextModels.map(model => ({ name: model.config.name })),
});
